// src/pages/reportes/components/ReporteOperativo.tsx
import { Card, Spinner, Badge } from '@/components/ui';
import {
  Ticket,
  Clock,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Activity,
} from 'lucide-react';
import { useReporteOperativo } from '@/hooks/queries/useReportes';
import type { ReporteOperativo as ReporteOperativoType } from '@/types/reporte';

interface ReporteOperativoProps {
  fechaInicio: string;
  fechaFin: string;
  clienteId?: string;
}

export function ReporteOperativo({ fechaInicio, fechaFin, clienteId }: ReporteOperativoProps) {
  const { data: response, isLoading, error } = useReporteOperativo(fechaInicio, fechaFin, clienteId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-red-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-4" />
          <p>Error al cargar el reporte operativo</p>
        </div>
      </Card>
    );
  }

  if (!response) {
    return (
      <Card className="p-6">
        <p className="text-center text-gray-500">No hay datos disponibles para el periodo seleccionado</p>
      </Card>
    );
  }

  // Extraer datos del reporte
  const reporte = response.datos as unknown as ReporteOperativoType | undefined;

  if (!reporte) {
    return (
      <Card className="p-6">
        <p className="text-center text-gray-500">No hay datos disponibles para el periodo seleccionado</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cards de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Ticket className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Tickets Creados</p>
              <p className="text-2xl font-bold">{reporte.resumen?.tickets_creados ?? 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Tickets Resueltos</p>
              <p className="text-2xl font-bold">{reporte.resumen?.tickets_resueltos ?? 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Horas Registradas</p>
              <p className="text-2xl font-bold">{(reporte.resumen?.horas_registradas ?? 0).toFixed(1)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Activity className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Actividades Completadas</p>
              <p className="text-2xl font-bold">{reporte.resumen?.actividades_completadas ?? 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Seccion Tickets */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5" />
          Tickets
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Por Estado */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Por Estado</h4>
            <div className="space-y-2">
              {reporte.tickets?.por_estado && Object.entries(reporte.tickets.por_estado).map(([estado, cantidad]) => (
                <div key={estado} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 capitalize">{estado.toLowerCase().replace('_', ' ')}</span>
                  <Badge variant="default">{cantidad}</Badge>
                </div>
              ))}
              {(!reporte.tickets?.por_estado || Object.keys(reporte.tickets.por_estado).length === 0) && (
                <p className="text-sm text-gray-500">Sin datos</p>
              )}
            </div>
          </div>

          {/* Por Prioridad */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Por Prioridad</h4>
            <div className="space-y-2">
              {reporte.tickets?.por_prioridad && Object.entries(reporte.tickets.por_prioridad).map(([prioridad, cantidad]) => (
                <div key={prioridad} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 capitalize">{prioridad.toLowerCase()}</span>
                  <Badge variant="default">{cantidad}</Badge>
                </div>
              ))}
              {(!reporte.tickets?.por_prioridad || Object.keys(reporte.tickets.por_prioridad).length === 0) && (
                <p className="text-sm text-gray-500">Sin datos</p>
              )}
            </div>
          </div>
        </div>

        {/* Metricas de tickets */}
        <div className="mt-6 pt-4 border-t grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Tiempo promedio resolución</p>
            <p className="text-lg font-semibold">
              {Math.round(reporte.tickets?.tiempo_promedio_resolucion_min ?? 0)} min
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">SLA cumplido</p>
            <p className="text-lg font-semibold">{(reporte.tickets?.sla_cumplido_pct ?? 0).toFixed(1)}%</p>
          </div>
        </div>
      </Card>

      {/* Seccion Conversaciones */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Conversaciones
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{reporte.conversaciones?.activas ?? 0}</p>
            <p className="text-sm text-gray-500">Activas</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">{reporte.conversaciones?.sin_respuesta ?? 0}</p>
            <p className="text-sm text-gray-500">Sin respuesta</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">
              {Math.round(reporte.conversaciones?.tiempo_promedio_respuesta_min ?? 0)} min
            </p>
            <p className="text-sm text-gray-500">Tiempo promedio respuesta</p>
          </div>
        </div>
      </Card>

      {/* Seccion Tiempo */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Tiempo
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Horas totales</span>
              <span className="text-lg font-semibold">{(reporte.tiempo?.horas_totales ?? 0).toFixed(1)}h</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Porcentaje facturable</span>
              <span className="text-lg font-semibold">{(reporte.tiempo?.porcentaje_facturable ?? 0).toFixed(1)}%</span>
            </div>
          </div>

          {/* Grafico placeholder de tendencia */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">Tendencia de horas</p>
            <div className="h-24 flex items-end gap-1">
              {(reporte.tiempo?.tendencia ?? []).slice(-7).map((item, idx) => {
                const maxVal = Math.max(...(reporte.tiempo?.tendencia ?? []).map(t => t.valor), 1);
                return (
                  <div
                    key={idx}
                    className="flex-1 bg-blue-400 rounded-t"
                    style={{
                      height: `${Math.max(10, (item.valor / maxVal) * 100)}%`,
                    }}
                    title={`${item.fecha}: ${item.valor}h`}
                  />
                );
              })}
              {(!reporte.tiempo?.tendencia || reporte.tiempo.tendencia.length === 0) && (
                <p className="text-sm text-gray-400 w-full text-center">Sin datos de tendencia</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Seccion Actividades */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Actividades
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{reporte.actividades?.completadas ?? 0}</p>
            <p className="text-sm text-gray-500">Completadas</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{reporte.actividades?.pendientes ?? 0}</p>
            <p className="text-sm text-gray-500">Pendientes</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-600">{reporte.actividades?.vencidas ?? 0}</p>
            <p className="text-sm text-gray-500">Vencidas</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
